import { useState, useCallback, useMemo } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Card, CardContent, CopyButton } from '@devkit/ui';
import { Wand2, Search, Sparkles } from 'lucide-react';
import { commonPatterns, testRegex, type CommonPattern } from '@devkit/tools-regex';
import { trackToolExecute, trackCopy } from '@devkit/analytics';

export default function RegexGeneratorPage() {
  const [selectedId, setSelectedId] = useState<string>('email');
  const [pattern, setPattern] = useState<string>(commonPatterns[0].pattern);
  const [description, setDescription] = useState<string>(commonPatterns[0].description);
  const [sampleInput, setSampleInput] = useState<string>(commonPatterns[0].example.input);
  const [search, setSearch] = useState('');

  const matches = useMemo(() => {
    if (!pattern) return [];
    const result = testRegex({ pattern, flags: 'g', testString: sampleInput });
    return result.success ? result.matches ?? [] : [];
  }, [pattern, sampleInput]);

  const filteredPatterns = useMemo(() => {
    if (!search.trim()) return commonPatterns;
    const q = search.toLowerCase();
    return commonPatterns.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q)
    );
  }, [search]);

  const handleSelectPattern = useCallback(
    (p: CommonPattern) => {
      setSelectedId(p.id);
      setPattern(p.pattern);
      setDescription(p.description);
      setSampleInput(p.example.input);
      trackToolExecute('regex-generator', `select-${p.id}`);
    },
    []
  );

  const copyPattern = () => {
    trackCopy('regex-generator');
  };

  return (
    <ToolLayout slug="regex-generator">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pattern Library */}
        <Card className="lg:col-span-1">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-primary" />
              <h2 className="font-semibold text-white">Pattern Library</h2>
            </div>

            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search patterns..."
                className="w-full pl-9 pr-3 py-2 bg-surface border border-slate-700 rounded-lg text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary"
              />
            </div>

            <div className="space-y-1.5 max-h-[500px] overflow-auto">
              {filteredPatterns.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleSelectPattern(p)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors ${
                    selectedId === p.id
                      ? 'bg-primary/15 border border-primary/40'
                      : 'bg-surface/40 border border-slate-700/50 hover:bg-surface/70 hover:border-slate-600'
                  }`}
                >
                  <div className="font-medium text-white text-sm">{p.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{p.description}</div>
                </button>
              ))}

              {filteredPatterns.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-8">No patterns found.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Detail + Tester */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <Wand2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <h2 className="font-semibold text-white">
                    {commonPatterns.find((p) => p.id === selectedId)?.name}
                  </h2>
                  <p className="text-sm text-slate-400 mt-1">{description}</p>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-300">Pattern</label>
                  <CopyButton text={pattern} onCopy={copyPattern} />
                </div>
                <input
                  type="text"
                  value={pattern}
                  onChange={(e) => setPattern(e.target.value)}
                  className="w-full px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-green-300 focus:outline-none focus:border-primary"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Test Input
                </label>
                <textarea
                  value={sampleInput}
                  onChange={(e) => setSampleInput(e.target.value)}
                  placeholder="Paste sample text to test the pattern against..."
                  className="w-full h-32 px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-primary resize-y"
                />
              </div>

              {/* Highlighted output */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-300">
                    Matches ({matches.length})
                  </label>
                </div>
                <div className="w-full min-h-[80px] px-4 py-3 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-200 whitespace-pre-wrap break-words">
                  {sampleInput ? (
                    highlightMatches(sampleInput, matches)
                  ) : (
                    <span className="text-slate-500">Type or paste sample text above.</span>
                  )}
                </div>
              </div>

              {matches.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Captured</p>
                  <ul className="space-y-1.5">
                    {matches.map((m, i) => (
                      <li
                        key={i}
                        className="text-sm font-mono bg-slate-900/60 px-3 py-2 rounded text-green-300"
                      >
                        <span className="text-slate-500">[{i}]</span> {m.text}
                        {m.groups.length > 0 && (
                          <span className="text-slate-500 ml-2">
                            groups: [{m.groups.join(', ')}]
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </ToolLayout>
  );
}

function highlightMatches(text: string, matches: { text: string; index: number }[]): React.ReactNode {
  if (matches.length === 0) return text;

  const sorted = [...matches].sort((a, b) => a.index - b.index);
  const parts: React.ReactNode[] = [];
  let cursor = 0;

  for (const m of sorted) {
    if (m.index < cursor) continue; // skip overlapping
    if (m.index > cursor) parts.push(text.slice(cursor, m.index));
    parts.push(
      <mark key={m.index} className="bg-primary/30 text-primary font-medium rounded px-0.5">
        {m.text}
      </mark>
    );
    cursor = m.index + m.text.length;
  }

  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}
